/**
 * A row of choices is a TRAY with its name in line (DEC-321, built 2026-10-27): Transpose, Capo, Quantize, Waves Tune,
 * Scene copy and the Timeline Launcher sit in the same recessed tray as a button grid's pads, with the item's Name in a
 * 10 px band inside it (DEC-316's title, at the same corners). A separate label that says the same words hides itself
 * while the title shows (Label.tsx). No name, no band.
 *
 * The root of each row kind spreads `trayAttrs(item)` and renders `<TrayTitle item={item} />` first.
 */

import { Show } from 'solid-js';
import type { LayoutItem } from '../layout/projectStore.ts';
import type { GridTitleAt } from '../layout/buttonLook.ts';

/** The kinds drawn as a tray with an in-line title. */
export const TRAY_KINDS: ReadonlySet<LayoutItem['kind']> = new Set<LayoutItem['kind']>([
  'transpose', 'capo', 'recQuant', 'wavesAutotune', 'sceneCopy', 'timelineLauncher',
]);

type Titled = { kind?: LayoutItem['kind']; name?: string | undefined; titleAt?: GridTitleAt | undefined };

/** What a tray is called when it has no Name of its own: what the row IS, as the study titled them on the operator's
 *  page 2 (their rows carried no Name, only a separate label saying the same, which now hides behind it). */
const KIND_TITLE: Partial<Record<LayoutItem['kind'], string>> = {
  transpose: 'Transpose', capo: 'Capo', recQuant: 'Quantize', wavesAutotune: 'Tune', sceneCopy: 'Scene copy', timelineLauncher: 'Launcher',
};

/** The words a tray shows: its own Name, else its kind's. */
export function trayTitleText(item: Titled): string {
  return (item.name ?? '').trim() || (item.kind ? KIND_TITLE[item.kind] ?? '' : '');
}

export function trayTitleAt(item: Titled): GridTitleAt {
  return trayTitleText(item) ? item.titleAt ?? 'tl' : 'off';
}

/** The tray's attributes, for the row's root element. */
export function trayAttrs(item: Titled): { 'data-tray': string; 'data-title': GridTitleAt } {
  return { 'data-tray': '', 'data-title': trayTitleAt(item) };
}

export function TrayTitle(props: { item: Titled }) {
  return (
    <Show when={trayTitleAt(props.item) !== 'off'}>
      <span class="bg-title"><span class="bg-title-text">{trayTitleText(props.item)}</span></span>
    </Show>
  );
}
